import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Link2, MousePointerClick, Target, Plus, 
  AlertTriangle, TrendingUp, Calendar
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [globalAnalytics, setGlobalAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, globalRes] = await Promise.all([
        fetch(`${API}/analytics/overview`, { credentials: "include" }),
        fetch(`${API}/analytics/global?days=7`, { credentials: "include" })
      ]);

      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
      if (globalRes.ok) {
        setGlobalAnalytics(await globalRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#10B981', '#000000', '#71717A'];

  const typeData = globalAnalytics?.clicks_by_type ? [
    { name: 'WhatsApp', value: globalAnalytics.clicks_by_type.whatsapp },
    { name: 'Grupos', value: globalAnalytics.clicks_by_type.whatsapp_group },
    { name: 'URLs', value: globalAnalytics.clicks_by_type.url }
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <Sidebar user={user} />

      {/* Main Content */}
      <main className="md:ml-64 pt-14 md:pt-0 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-black mb-2">Panel de Control</h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Bienvenido, {user?.name?.split(" ")[0] || "Usuario"}
            </p>
          </div>

          {/* Ban Alert */}
          {analytics?.ban_alerts > 0 && (
            <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800 text-sm md:text-base">
                    {analytics.ban_alerts} destino(s) con posible bloqueo
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="md:ml-auto border-red-300 text-red-600 hover:bg-red-100 w-full md:w-auto"
                onClick={() => window.location.href = "/enlaces"}
              >
                Ver Enlaces
              </Button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 md:h-32 skeleton"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Stats Grid - 2 columns on mobile, 5 on desktop */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
                <Card className="border-border rounded-none">
                  <CardContent className="p-4 md:p-6">
                    <p className="label-swiss mb-1 md:mb-2 text-[10px] md:text-xs">Clics Hoy</p>
                    <p className="text-2xl md:text-4xl font-heading font-black text-primary">{analytics?.today_clicks || 0}</p>
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none">
                  <CardContent className="p-4 md:p-6">
                    <p className="label-swiss mb-1 md:mb-2 text-[10px] md:text-xs">7 Días</p>
                    <p className="text-2xl md:text-4xl font-heading font-black">{analytics?.recent_clicks || 0}</p>
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none">
                  <CardContent className="p-4 md:p-6">
                    <p className="label-swiss mb-1 md:mb-2 text-[10px] md:text-xs">Total</p>
                    <p className="text-2xl md:text-4xl font-heading font-black">{analytics?.total_clicks || 0}</p>
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none">
                  <CardContent className="p-4 md:p-6">
                    <p className="label-swiss mb-1 md:mb-2 text-[10px] md:text-xs">Enlaces</p>
                    <p className="text-2xl md:text-4xl font-heading font-black">
                      {analytics?.active_links || 0}
                      <span className="text-sm md:text-lg text-muted-foreground">/{analytics?.total_links || 0}</span>
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none col-span-2 md:col-span-1">
                  <CardContent className="p-4 md:p-6">
                    <p className="label-swiss mb-1 md:mb-2 text-[10px] md:text-xs">Destinos</p>
                    <p className="text-2xl md:text-4xl font-heading font-black">
                      {analytics?.active_destinations || 0}
                      <span className="text-sm md:text-lg text-muted-foreground">/{analytics?.total_destinations || 0}</span>
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                <Card className="border-border rounded-none lg:col-span-2">
                  <CardHeader className="pb-2 px-4 md:px-6">
                    <CardTitle className="font-heading text-base md:text-lg">Clics Últimos 7 Días</CardTitle>
                  </CardHeader>
                  <CardContent className="px-2 md:px-6">
                    {globalAnalytics?.clicks_by_day?.length > 0 ? (
                      <div className="h-48 md:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={globalAnalytics.clicks_by_day}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                            <YAxis tick={{ fontSize: 10 }} width={30} />
                            <Tooltip />
                            <Bar dataKey="clicks" fill="#10B981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-48 md:h-64 flex items-center justify-center text-muted-foreground text-sm">
                        No hay datos todavía
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none">
                  <CardHeader className="pb-2 px-4 md:px-6">
                    <CardTitle className="font-heading text-base md:text-lg">Por Tipo</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6">
                    {typeData.length > 0 ? (
                      <div className="h-48 md:h-64 flex flex-col items-center justify-center">
                        <ResponsiveContainer width="100%" height={140}>
                          <PieChart>
                            <Pie data={typeData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value">
                              {typeData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="flex flex-wrap justify-center gap-3 mt-2">
                          {typeData.map((entry, index) => (
                            <div key={entry.name} className="flex items-center gap-1 text-xs">
                              <div className="w-2 h-2" style={{ backgroundColor: COLORS[index] }}></div>
                              <span>{entry.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="h-48 md:h-64 flex items-center justify-center text-muted-foreground text-sm">
                        Sin datos
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions - Full width on mobile */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <Card className="border-border rounded-none">
                  <CardHeader className="pb-3 md:pb-4 px-4 md:px-6">
                    <CardTitle className="font-heading text-base md:text-lg">Acciones Rápidas</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 md:space-y-3 px-4 md:px-6">
                    <Button 
                      className="w-full justify-start rounded-none bg-primary hover:bg-black text-sm md:text-base"
                      onClick={() => window.location.href = "/enlaces"}
                      data-testid="create-link-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Nuevo Enlace
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-border rounded-none md:col-span-2">
                  <CardHeader className="pb-3 md:pb-4 px-4 md:px-6 flex flex-row items-center justify-between">
                    <CardTitle className="font-heading text-base md:text-lg">Top Enlaces</CardTitle>
                    <Button variant="ghost" size="sm" className="text-primary text-xs" onClick={() => window.location.href = "/enlaces"}>
                      Ver todos
                    </Button>
                  </CardHeader>
                  <CardContent className="px-4 md:px-6">
                    {globalAnalytics?.clicks_by_link?.length > 0 ? (
                      <div className="space-y-2">
                        {globalAnalytics.clicks_by_link.slice(0, 3).map((link, index) => (
                          <div 
                            key={link.link_id}
                            className="flex items-center justify-between p-2 md:p-3 border border-border cursor-pointer hover:border-foreground"
                            onClick={() => window.location.href = `/enlaces/${link.link_id}`}
                          >
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                              <span className="text-xs font-bold text-muted-foreground">#{index + 1}</span>
                              <div className="min-w-0">
                                <p className="font-medium text-sm truncate">{link.name}</p>
                                <p className="text-xs text-muted-foreground">/{link.slug}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-heading font-bold">{link.clicks}</p>
                              <p className="text-xs text-muted-foreground">clics</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 md:py-8 text-muted-foreground">
                        <Link2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No tienes enlaces todavía</p>
                        <Button className="mt-3 rounded-none text-sm" onClick={() => window.location.href = "/enlaces"}>
                          Crear primer enlace
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
